/* Jubi Form — shared behaviour for the custom contact forms
   (Commission, Trade application, …). One root per form: [data-jubi-form].

   Root data attributes:
     data-upload-url        worker endpoint (POST raw file → {url})
     data-klaviyo-company   public key; omit to skip the event
     data-klaviyo-metric    event name, e.g. "Started a Commission"
     data-klaviyo-list      optional list id for the opt-in checkbox [data-jf-subscribe]
     data-klaviyo-source    custom_source for that subscribe (default = metric)

   Upload groups (any number per form): [data-jf-upload] wrapping
     [data-jf-file] (file input) · [data-jf-upload-box] (label) ·
     [data-jf-upload-label] · [data-jf-chips] · [data-jf-upload-status] ·
     [data-jf-file-urls] (hidden field that carries the URLs into the email)
     group attrs: data-max-files (default 5) · data-required (blocks submit
     until at least one file is attached) · data-idle-label

   Files upload the moment they're chosen; submit is disabled while any
   upload is in flight. Submit is the native POST → Shopify emails hello@. */
(function () {
  'use strict';

  var KLAVIYO_REVISION = '2024-10-15';
  var MAX_BYTES = 8 * 1024 * 1024;

  function initUploadGroup(root, group, state) {
    var fileInput = group.querySelector('[data-jf-file]');
    var uploadBox = group.querySelector('[data-jf-upload-box]');
    var uploadLabel = group.querySelector('[data-jf-upload-label]');
    var status = group.querySelector('[data-jf-upload-status]');
    var chips = group.querySelector('[data-jf-chips]');
    var urlField = group.querySelector('[data-jf-file-urls]');
    var maxFiles = parseInt(group.dataset.maxFiles, 10) || 5;
    var idleLabel = group.dataset.idleLabel || (uploadLabel ? uploadLabel.textContent : 'Add files');
    var files = []; /* { url, name, isImage, kind } */

    function setStatus(message, isError) {
      if (!status) return;
      status.hidden = !message;
      status.textContent = message || '';
      status.classList.toggle('is-error', !!isError);
    }

    function sync() {
      if (urlField) urlField.value = files.map(function (f) { return f.url; }).join('\n');

      if (chips) {
        chips.innerHTML = '';
        files.forEach(function (f, i) {
          var wrap = document.createElement('span');
          wrap.className = 'jf__chip';
          if (f.isImage) {
            var img = document.createElement('img');
            img.src = f.url;
            img.alt = f.name || ('Uploaded file ' + (i + 1));
            img.loading = 'lazy';
            wrap.appendChild(img);
          } else {
            var doc = document.createElement('span');
            doc.className = 'jf__chip-doc';
            doc.setAttribute('data-kind', f.kind);
            doc.textContent = f.name || ('File ' + (i + 1));
            doc.title = f.name || '';
            wrap.appendChild(doc);
          }
          var x = document.createElement('button');
          x.type = 'button';
          x.className = 'jf__chip-x';
          x.setAttribute('aria-label', 'Remove ' + (f.name || 'file ' + (i + 1)));
          x.textContent = '×';
          x.addEventListener('click', function (e) {
            /* chips live inside the upload <label> — without this the
               click would also pop the file picker */
            e.preventDefault();
            e.stopPropagation();
            files.splice(i, 1);
            sync();
            setStatus('', false);
          });
          wrap.appendChild(x);
          chips.appendChild(wrap);
        });
        chips.hidden = files.length === 0;
      }

      if (uploadBox) uploadBox.classList.toggle('is-done', files.length > 0);
      if (uploadLabel) {
        if (!files.length) {
          uploadLabel.textContent = idleLabel;
        } else if (files.length >= maxFiles) {
          uploadLabel.textContent = files.length + (files.length === 1 ? ' file attached ✓' : ' files attached ✓');
        } else {
          uploadLabel.textContent = files.length + (files.length === 1 ? ' file' : ' files') + ' attached ✓ — tap to add another';
        }
      }
    }

    function guessType(file) {
      if (file.type) return file.type;
      return /\.pdf$/i.test(file.name || '') ? 'application/pdf' : 'image/jpeg';
    }

    function uploadOne(file) {
      var type = guessType(file);
      /* Turnstile token per upload (assets/jubi-turnstile.js); the upload Worker verifies it */
      var tokenP = window.JubiTurnstile ? window.JubiTurnstile.token(uploadBox) : Promise.resolve(null);
      return tokenP.then(function (tok) {
        var headers = { 'Content-Type': type };
        if (tok) headers['X-Turnstile-Token'] = tok;
        return fetch(root.dataset.uploadUrl, { method: 'POST', headers: headers, body: file });
      })
        .then(function (res) { return res.json(); })
        .then(function (data) {
          if (!data || !data.url) return null;
          var isImage = type.indexOf('image/') === 0;
          return { url: data.url, name: file.name || '', isImage: isImage, kind: isImage ? 'IMG' : 'PDF' };
        })
        .catch(function () { return null; });
    }

    if (fileInput) {
      fileInput.addEventListener('change', function () {
        var picked = Array.prototype.slice.call(fileInput.files || []);
        /* clear immediately so picking the SAME file again still fires */
        fileInput.value = '';
        if (!picked.length) return;

        var room = maxFiles - files.length;
        if (room <= 0) {
          setStatus((maxFiles === 1 ? 'One file is attached' : maxFiles + ' files is the limit') + ' — tap the × to swap it out.', false);
          return;
        }

        var skippedLarge = picked.length;
        picked = picked.filter(function (f) { return f.size <= MAX_BYTES; });
        skippedLarge -= picked.length;
        var skippedExtra = Math.max(0, picked.length - room);
        picked = picked.slice(0, room);

        if (!picked.length) {
          setStatus('That file is over 8 MB — a smaller export or a photo of the page works too.', true);
          return;
        }

        state.uploading += 1;
        state.syncSubmit();
        setStatus('Uploading ' + picked.length + (picked.length === 1 ? ' file…' : ' files…'), false);

        Promise.all(picked.map(uploadOne)).then(function (results) {
          var good = results.filter(Boolean);
          files = files.concat(good);
          sync();

          var notes = [];
          if (good.length < picked.length) notes.push((picked.length - good.length) + ' upload(s) failed — please try again.');
          if (skippedLarge) notes.push(skippedLarge + ' file(s) skipped (over 8 MB).');
          if (skippedExtra) notes.push('Only ' + room + ' more fit — the first ' + picked.length + (picked.length === 1 ? ' was' : ' were') + ' added.');
          setStatus(notes.join(' '), good.length < picked.length);

          state.uploading -= 1;
          state.syncSubmit();
        });
      });
    }

    return {
      isRequired: group.hasAttribute('data-required'),
      hasFiles: function () { return files.length > 0; },
      flagMissing: function () {
        setStatus('Please attach this file before sending.', true);
        if (uploadBox) uploadBox.classList.add('is-missing');
        try { uploadBox.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (err) { /* ignore */ }
      },
      urls: function () { return urlField ? urlField.value : ''; }
    };
  }

  function init(root) {
    if (root.dataset.jfInit) return;
    root.dataset.jfInit = 'true';

    var form = root.querySelector('form.jf__form, form[action*="/contact"]');
    var submitBtn = root.querySelector('[data-jf-submit]');
    var subscribeBox = root.querySelector('[data-jf-subscribe]');

    var state = {
      uploading: 0,
      syncSubmit: function () { if (submitBtn) submitBtn.disabled = state.uploading > 0; }
    };

    var success = root.querySelector('.jf__success');
    if (success) {
      /* Success view: land at the top of the section (counteracting the
         contact-form anchor scroll), focus quietly */
      if (window.location.hash) {
        try {
          history.replaceState(null, '', window.location.pathname + window.location.search);
        } catch (err) { /* ignore */ }
      }
      var toTop = function () {
        var top = root.getBoundingClientRect().top + window.pageYOffset - 80;
        window.scrollTo(0, Math.max(0, top));
      };
      toTop();
      setTimeout(toTop, 0);
      window.addEventListener('load', function () { setTimeout(toTop, 0); });
      try {
        success.focus({ preventScroll: true });
      } catch (err) {
        success.focus();
      }
    }

    var source = 'direct';
    try {
      source = new URLSearchParams(window.location.search).get('src') || 'direct';
    } catch (err) { /* older browsers — keep 'direct' */ }
    var sourceField = root.querySelector('[data-jf-source]');
    if (sourceField) sourceField.value = source;

    /* A design link arrives from the Studio's "Request another construction"
       (?design=<saved-design URL>). Stamp it into the hidden field so the
       studio opens exactly what the customer built; when absent the field
       stays disabled so the e-mail omits the line. */
    var designField = root.querySelector('[data-jf-design]');
    if (designField) {
      var design = '';
      try { design = new URLSearchParams(window.location.search).get('design') || ''; } catch (err) { /* keep empty */ }
      if (/^https?:\/\/(www\.)?shop-jubi\.com\//.test(design)) {
        designField.value = design;
        designField.disabled = false;
      }
    }

    /* The Studio's "Larger than 20 ft? Send your size" link (Oct 9 2026)
       arrives with ?design_name, ?shape and ?size. Fill the matching fields
       so the customer only has to add their name and email; typed values
       always win (only empty fields are filled). */
    try {
      var pre = new URLSearchParams(window.location.search);
      var fill = function (name, key) {
        var field = root.querySelector('[name="' + name + '"]');
        var val = (pre.get(key) || '').trim().slice(0, 200);
        if (field && val && !field.value) field.value = val;
      };
      fill('contact[Design and color]', 'design_name');
      fill('contact[Size]', 'size');
      /* Contact form (Oct 10 2026): the Studio's oversize link lands here instead of the custom-sizes page.
         Compose the message from the design, shape and size, and pick a custom/size topic when one exists. */
      var msg = root.querySelector('[name="contact[Message]"]');
      var dn = (pre.get('design_name') || '').trim().slice(0, 120);
      if (msg && !msg.value && dn && pre.get('size')) {
        var parts = ['Custom size request: ' + dn];
        if (pre.get('shape')) parts.push((pre.get('shape') || '').trim().slice(0, 40));
        parts.push((pre.get('size') || '').trim().slice(0, 60));
        var lines = [parts.join(', ') + '.'];
        if (pre.get('design')) lines.push('My design: ' + (pre.get('design') || '').trim().slice(0, 300));
        lines.push('');
        msg.value = lines.join('\n');
        var topic = root.querySelector('select[name="contact[Topic]"]');
        if (topic && !topic.value) {
          Array.prototype.some.call(topic.options, function (o) {
            if (/custom|size/i.test(o.textContent || '')) { topic.value = o.value || o.textContent; return true; }
            return false;
          });
        }
      }
      var shape = (pre.get('shape') || '').trim();
      if (shape) {
        Array.prototype.forEach.call(root.querySelectorAll('input[name="contact[Shape]"]'), function (r) {
          if (r.value === shape) r.checked = true;
        });
      }
    } catch (err) { /* older browsers — leave the form empty */ }

    var groups = Array.prototype.map.call(root.querySelectorAll('[data-jf-upload]'), function (g) {
      return initUploadGroup(root, g, state);
    });

    if (form) {
      /* Blocking checks run in the CAPTURE phase on window, before any other submit listener (Oct 9 2026: Shopify's
         contact-form spam protection takes over the submit and posts the form itself, so a plain preventDefault in
         our bubble-phase handler showed the "attach this file" message but the form still went through). */
      var blockSubmit = function (e) {
        if (e.target !== form) return;
        var stop = false;
        if (state.uploading > 0) stop = true;
        /* required uploads: the hidden URL field must carry something */
        var missing = groups.filter(function (g) { return g.isRequired && !g.hasFiles(); });
        if (!stop && missing.length) { missing[0].flagMissing(); stop = true; }
        if (stop) { e.preventDefault(); e.stopImmediatePropagation(); }
      };
      window.addEventListener('submit', blockSubmit, true);

      form.addEventListener('submit', function (e) {
        if (e.defaultPrevented) return;

        var companyId = root.dataset.klaviyoCompany;
        var metric = root.dataset.klaviyoMetric;
        var emailInput = form.querySelector('input[name="contact[email]"]');
        var email = emailInput ? emailInput.value.trim() : '';
        if (!companyId || !metric || !email || !window.fetch) return;

        /* Every contact[...] field becomes an event property, keyed by its
           label inside the brackets. Radios report the checked value. */
        var props = {};
        Array.prototype.forEach.call(form.querySelectorAll('[name^="contact["]'), function (el) {
          var m = /^contact\[(.+)\]$/.exec(el.name);
          if (!m) return;
          var key = m[1];
          if (key === 'email' || key === 'Request') return;
          if (el.type === 'radio') {
            if (el.checked) props[key] = el.value;
            else if (!(key in props)) props[key] = '';
            return;
          }
          if (el.type === 'checkbox') { props[key] = el.checked ? (el.value || 'yes') : ''; return; }
          props[key] = el.value;
        });
        props['Request Source'] = (sourceField && sourceField.value) || source;

        var eventPayload = {
          data: {
            type: 'event',
            attributes: {
              properties: props,
              metric: { data: { type: 'metric', attributes: { name: metric } } },
              profile: { data: { type: 'profile', attributes: { email: email } } }
            }
          }
        };

        fetch('https://a.klaviyo.com/client/events/?company_id=' + encodeURIComponent(companyId), {
          method: 'POST',
          keepalive: true,
          headers: { 'Content-Type': 'application/vnd.api+json', revision: KLAVIYO_REVISION },
          body: JSON.stringify(eventPayload)
        }).catch(function () {});

        if (subscribeBox && subscribeBox.checked && root.dataset.klaviyoList) {
          var subPayload = {
            data: {
              type: 'subscription',
              attributes: {
                custom_source: root.dataset.klaviyoSource || metric,
                profile: { data: { type: 'profile', attributes: { email: email } } }
              },
              relationships: { list: { data: { type: 'list', id: root.dataset.klaviyoList } } }
            }
          };
          fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(companyId), {
            method: 'POST',
            keepalive: true,
            headers: { 'Content-Type': 'application/vnd.api+json', revision: KLAVIYO_REVISION },
            body: JSON.stringify(subPayload)
          }).catch(function () {});
        }
        /* form submits natively → Shopify emails the store address */
      });
    }
  }

  function initAll() {
    document.querySelectorAll('[data-jubi-form]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var el = event.target.querySelector('[data-jubi-form]');
    if (el) init(el);
  });
})();
